import { isEntityAccount } from "$lib/util/account"
import type { FormDataAdapter } from "$lib/util/model-adapters"
import { PatchBuilder } from "$lib/util/model-adapters/patch.ts"
import { LIQUIDITY_NEEDS } from "@tastyworks/shared-utils/misc/constants"
import {
  type AccountOpeningApplication,
  ACCOUNT_ACTIVITY_TYPES,
  ACCOUNT_TYPE_CHOICES,
  EXPECTED_WITHDRAWAL_TYPES,
  INITIAL_DEPOSIT_TYPES,
  INITIAL_FUNDING_SOURCE,
  REFERRAL_SOURCES,
} from "@tastyworks/tastyworks-api"
import {
  type InferType,
  Schema,
  boolean,
  mixed,
  number,
  object,
  string,
} from "yup"

function isPersonalReferral(
  [initialContact]: REFERRAL_SOURCES[],
  schema: Schema
) {
  return initialContact === REFERRAL_SOURCES.PERSONAL_REFERRAL
    ? schema.required()
    : schema
}

export const accountActivityBankingSchema = object({
  accountType: mixed<ACCOUNT_TYPE_CHOICES>(),
  primaryBanking: string().required(),
  valueInitialDeposit: number()
    .typeError("Must be a number")
    .positive()
    .required(),
  initialDepositType: mixed<INITIAL_DEPOSIT_TYPES>()
    .oneOf(Object.values(INITIAL_DEPOSIT_TYPES))
    .required(),
  initialFundingSource: mixed<INITIAL_FUNDING_SOURCE>()
    .oneOf(Object.values(INITIAL_FUNDING_SOURCE))
    .when("accountType", ([accountType], schema) =>
      isEntityAccount(accountType) ? schema.required() : schema.nullable()
    ),
})

export const accountActivityDetailsSchema = object({
  isForeigner: boolean(),
  accountActivityType: mixed<ACCOUNT_ACTIVITY_TYPES>()
    .oneOf(Object.values(ACCOUNT_ACTIVITY_TYPES))
    .required(),
  liquidityNeeds: mixed<LIQUIDITY_NEEDS>()
    .oneOf(Object.values(LIQUIDITY_NEEDS))
    .required(),
  expectedWithdrawals: mixed<EXPECTED_WITHDRAWAL_TYPES>()
    .oneOf(Object.values(EXPECTED_WITHDRAWAL_TYPES))
    .required(),
  initialContact: mixed<REFERRAL_SOURCES>()
    .oneOf(Object.values(REFERRAL_SOURCES))
    .when("isForeigner", ([isForeigner], schema) =>
      isForeigner ? schema.required() : schema.nullable()
    ),
  referredName: string().when("initialContact", isPersonalReferral),
  referredRelationship: string().when("initialContact", isPersonalReferral),
  isReferredToBroker: boolean().required(),
})

export const accountActivityBankingAdapter = {
  toForm: (application, formData) => {
    formData.primaryBanking = application.primaryBanking ?? undefined
    formData.initialDepositType = application.initialDepositType ?? undefined
    formData.valueInitialDeposit = application.valueInitialDeposit ?? undefined
    formData.initialFundingSource =
      application.initialFundingSource ?? undefined
    return formData
  },
  toPatch: (data) =>
    new PatchBuilder<AccountOpeningApplication>()
      .string("primaryBanking", data.primaryBanking)
      .string("initialDepositType", data.initialDepositType)
      .number("valueInitialDeposit", data.valueInitialDeposit)
      .string("initialFundingSource", data.initialFundingSource ?? undefined)
      .build(),
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof accountActivityBankingSchema>
  >,
  "toForm" | "toPatch"
>

export const accountActivityDetailsAdapter = {
  toForm: (application, formData) => {
    formData.accountActivityType = application.accountActivityType ?? undefined
    formData.expectedWithdrawals = application.expectedWithdrawals ?? undefined
    formData.liquidityNeeds = application.liquidityNeeds ?? undefined
    formData.initialContact = application.isReferredToBroker
      ? REFERRAL_SOURCES.PERSONAL_REFERRAL
      : (application.initialContact ?? undefined)
    formData.isReferredToBroker = application.isReferredToBroker ?? false
    formData.referredName = application.referredName ?? undefined
    formData.referredRelationship =
      application.referredRelationship ?? undefined
    return formData
  },
  toPatch: (data) => {
    const isPersonalReferral =
      data.initialContact === REFERRAL_SOURCES.PERSONAL_REFERRAL
    const patch = new PatchBuilder<AccountOpeningApplication>()
      .string("accountActivityType", data.accountActivityType)
      .string("expectedWithdrawals", data.expectedWithdrawals)
      .string("liquidityNeeds", data.liquidityNeeds)
      .string("referredRelationship", data.referredRelationship)
      .string("referredName", data.referredName)
      .build()

    if (isPersonalReferral) {
      return { ...patch, initialContact: null, isReferredToBroker: true }
    }
    if (data.initialContact) {
      return {
        ...patch,
        initialContact: data.initialContact,
        isReferredToBroker: false,
      }
    }
    return patch
  },
} satisfies Pick<
  FormDataAdapter<
    AccountOpeningApplication,
    InferType<typeof accountActivityDetailsSchema>
  >,
  "toForm" | "toPatch"
>

export const accountActivitySchema = object({
  bankingInformation: accountActivityBankingSchema,
  accountActivityDetails: accountActivityDetailsSchema,
})
